import { isNil, isNull } from 'lodash';

import type {
  Country,
  NameSuffix,
  PutAkelaProfileRequest,
  PutSBProfileRequest,
  State,
} from '@appTypes/esb';

import { phoneTypeIds, usAkelaCountryId } from '../constants';
import { omitNullish, omitUndefinedNullables } from './omitHelpers';
import { trimField, trimObject } from './trimFields';

type Phone = {
  id?: number;
  typeId?: number;
  phoneCountryId?: number;
  mobilePhoneCarrier?: string;
  areaCode?: string;
  prefix?: string;
  lineNumber?: string;
  isPrimary?: boolean;
  extension?: string;
};

const getCountryName = (
  countryId: string | undefined,
  countries: Country[],
) => {
  if (countryId == String(usAkelaCountryId)) {
    return 'US';
  }

  const foundCountry = countries.find(c => c.id == countryId);
  return foundCountry ? foundCountry.short : undefined;
};

const getStateName = (stateId: string | undefined, states: State[]) => {
  const foundState = states.find(s => s.id == stateId);
  return foundState ? foundState.short : undefined;
};

const getSuffixName = (
  nameSuffixId: string | undefined | null,
  suffixes: NameSuffix[],
) => {
  const foundSuffix = suffixes.find(s => s.id == nameSuffixId);
  return foundSuffix ? foundSuffix.short : undefined;
};

const getPhoneFormatted = (phone: Phone | undefined) => {
  if (isNil(phone)) return undefined;
  const { areaCode, prefix, lineNumber } = phone;
  return `${areaCode}${prefix}${lineNumber}`;
};

type FormatSBProfile = {
  profile: PutAkelaProfileRequest;
  countries: Country[];
  states: State[];
  suffixes: NameSuffix[];
};

export default function formatSBProfile({
  profile,
  countries = [],
  states = [],
  suffixes = [],
}: FormatSBProfile): PutSBProfileRequest {
  const {
    firstName,
    lastName,
    middleName,
    nickName,
    nameSuffixId,
    addresses,
    emails,
    phones,
    sbExtendedProfile,
    schoolName,
  } = profile;

  const {
    oaActive,
    oaMember,
    oaMemberNumber,
    oaElectionDate,
    oaOrdealDate,
    oaBrotherhoodDate,
    oaVigilDate,
    talentReleaseUserId,
    swimmingClassification,
    swimmingClassificationDate,
    annualHealthRecordABDate,
    annualHealthRecordCDate,
  } = sbExtendedProfile || {};

  const foundEmail = emails?.find(email => email.isPrimary);

  const foundAddress = addresses?.find(address => address.isPrimary);
  const addressObj = foundAddress
    ? {
        address1: foundAddress.addressLine1,
        address2: foundAddress.addressLine2,
        address3: foundAddress.addressLine3,
        city: foundAddress.city,
        state: getStateName(`${foundAddress.stateId}`, states),
        zip: foundAddress.zip,
        country: getCountryName(`${foundAddress.countryId}`, countries),
      }
    : {};
  const foundHomePhone = phones?.find(
    ({ typeId }) => Number(typeId) === phoneTypeIds.HOME,
  );
  const foundMobilePhone: Phone | undefined = phones?.find(
    ({ typeId }) => Number(typeId) === phoneTypeIds.MOBILE,
  );
  const foundWorkPhone = phones?.find(
    ({ typeId }) => Number(typeId) === phoneTypeIds.WORK,
  );

  // talentReleaseDateRaw is dtoDateTimeFormat which SB requires
  const sbExtendedProfileExt =
    sbExtendedProfile as PutAkelaProfileRequest['sbExtendedProfile'] & {
      talentReleaseDateRaw: string;
    };
  const talentReleaseDateRaw =
    sbExtendedProfileExt?.talentReleaseDateRaw ?? undefined;

  const nullOmitedData = omitNullish<PutSBProfileRequest>({
    firstName: trimField(firstName),
    lastName: trimField(lastName),
    middleName: trimField(middleName),
    nickName: trimField(nickName),
    suffix: getSuffixName(`${nameSuffixId}`, suffixes),
    email: trimField(foundEmail ? foundEmail.email : foundEmail),
    schoolName: trimField(schoolName),
    homePhone: getPhoneFormatted(foundHomePhone),
    mobilePhone: getPhoneFormatted(foundMobilePhone),
    workPhone: getPhoneFormatted(foundWorkPhone),
    mobilePhoneCarrier: foundMobilePhone?.mobilePhoneCarrier,
    ...trimObject(addressObj),
    oaActive,
    oaMember,
    oaMemberNumber,
    talentReleaseUserId: talentReleaseUserId
      ? Number(talentReleaseUserId)
      : undefined,
    talentReleaseDate: talentReleaseDateRaw,
    swimmingClassification,
    swimmingClassificationDate,
    annualHealthRecordABDate,
    annualHealthRecordCDate,
  });

  // The following fields use null to remove data from Data base
  const nullableValues: Record<string, string | null> = omitUndefinedNullables({
    oaElectionDate,
    oaOrdealDate,
    oaBrotherhoodDate,
    oaVigilDate,
  });
  Object.entries(nullableValues).forEach(([key, value]) => {
    nullableValues[key] = isNull(value) ? '' : value;
  });

  return {
    ...nullOmitedData,
    ...nullableValues,
  };
}
